/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';
import { white, universalStyles, poslumaDarkBlue } from '../../../../app/theme';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import CaseStudy from '../../../micro/caseStudy/CaseStudy';
import FindingsPanel from '../../../micro/FindingsPanel/FindingsPanel';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '100%',
  },
  heading: {
    paddingTop: 30,
  },
  pageList: {
    marginBottom: 30,
  },
  whiteText: {
    color: white,
  },
  image: {
    width: '80%',
    height: 'auto',
    marginLeft: 90,
    marginRight: 90,
    padding: '20px 0 20px 0',
  },
  image2: {
    width: '90%',
    height: 'auto',
    marginLeft: 60,
    marginRight: 60,
    padding: '20px 0 20px 0',
  },
  imageText: {
    fontSize: 12,
    textAlign: 'center',
    paddingTop: 15,
  },
  header: {
    width: '60%',
    paddingBottom: 40,
  },
  caseStudy: {
    paddingTop: 30,
    paddingBottom: 15,
  },
  findingsPanel: {
    paddingBottom: 30,
  },
  reviewFindingsText: {
    color: poslumaDarkBlue,
    paddingTop: 20,
  },
  abbreviations: {
    paddingTop: 40,
    margin: '0 0 12px 0',
    fontSize: 14,
  },
  abbreviationContent: {
    margin: '0 0 12px 0',
    fontSize: 14,
  },
  superscript: {
    fontSize: 11,
  },
}));

type InterpretationPage12Props = {
  setNextDisabled: (disabled: boolean) => void;
  setNextDisabledCS: (disabled: boolean) => void;
};

const InterpretationPage12 = ({ setNextDisabled, setNextDisabledCS }: InterpretationPage12Props): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      {/* Need image here */}
      <div className={classes.poslumaBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[2].INTERPRETATION_STEP_2.path}`}
          alt={`${IMAGE_PATHS.STEPS[2].INTERPRETATION_STEP_2.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 2:</Typography>
          <Typography variant="h3">Evaluate POSLUMA Uptake - Interpretation Methodology</Typography>
          <Typography variant="h4" className={classes.heading}>
            Prostate and Prostate Bed
          </Typography>
          <Typography variant="body1">
            <strong>Assessment Procedures</strong>
            <sup className={classes.superscript}>1</sup>:
          </Typography>
          <ul className={classes.poslumaList}>
            <li>The suggested SUV scale is 0-10</li>
            <li>
              Focally increased uptake within the prostate or prostate bed is consistent with primary or recurrent tumor
            </li>
            <li>Uptake post brachytherapy may be heterogeneous in appearance </li>
          </ul>
          <Typography variant="body1">
            <strong>Risk of Image Misinterpretation in Patients with Suspected Prostate Cancer Recurrence</strong>
            <sup className={classes.superscript}>1</sup>:
          </Typography>
          <ul className={classes.poslumaList}>
            <li>
              The Interpretation of POSLUMA PET may differ depending on imaging readers in patients with suspected
              recurrence of prostate cancer, particularly in the prostate/prostate bed region. Because of the associated
              risk of false positive interpretation, consider multidisciplinary consultation and histopathological
              confirmation when clinical decision-making hinges on flotufolastat F18 uptake only in the
              prostate/prostate bed region or only on uptake interpreted as borderline.
            </li>
          </ul>
          <Typography variant="body1" className={classes.reviewFindingsText}>
            <strong>See arrows for the location of the lesion and review findings</strong>
          </Typography>
          <img
            className={classes.image}
            src={`${BASE_URL}${IMAGE_PATHS.PROSTATE_BED.path}`}
            alt={`${IMAGE_PATHS.PROSTATE_BED.alt}`}
          />
          <Typography variant="body2">
            <strong>Findings:</strong>
          </Typography>
          <ul className={classes.poslumaList}>
            <li>SUV scale is 0-10</li>
            <li>Uptake of POSLUMA is very intense in the prostate bed</li>
            <li>Positive lesion is consistent with local recurrence</li>
          </ul>
          <Typography variant="body1" className={classes.reviewFindingsText}>
            <strong>See arrows for the location of the lesion and review findings</strong>
          </Typography>
          <img
            className={classes.image}
            src={`${BASE_URL}${IMAGE_PATHS.PROSTATE_BED_2.path}`}
            alt={`${IMAGE_PATHS.PROSTATE_BED_2.alt}`}
          />
          <Typography variant="body2">
            <strong>Findings:</strong>
          </Typography>
          <ul className={classes.poslumaList}>
            <li>SUV scale is 0-10</li>
            <li>Focally increased uptake of POSLUMA in the right seminal vesicle</li>
            <li>Positive lesion is consistent with local recurrence</li>
          </ul>
          <div className={classes.caseStudy}>
            <CaseStudy
              trainingId={2}
              module="poslumaInterpretation"
              page={12}
              index={0}
              setNextDisabledCS={setNextDisabledCS}
            />
          </div>
          <Typography variant="h4">Pelvic Lymph Nodes</Typography>
          <Typography variant="body1">
            <strong>Assessment Procedures</strong>
            <sup className={classes.superscript}>1</sup>:
          </Typography>
          <ul className={classes.poslumaList}>
            <li>The suggested SUV scale is 0-10</li>
            <li>Any pelvic lymph nodes with increased uptake may represent nodal metastases</li>
            <li>Assess the pararectal and presacral lymph nodes</li>
            <li>Before assessing the inguinal lymph nodes, rescale to a suggested SUV scale of 0-20</li>
          </ul>
          <Typography variant="body1">
            <strong>Possible variant uptake or interpretation challenge</strong>
            <sup className={classes.superscript}>1</sup>:
          </Typography>
          <ul className={classes.poslumaList}>
            <li>Correlate any symmetrical presacral uptake with potential sacral ganglia</li>
            <li>
              Uptake in inguinal, very distant external iliac, and pre-acetabular lymph nodes is unlikely to represent
              nodal metastases unless the uptake is clearly and markedly elevated
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>Match with correlative anatomical imaging</li>
            </ul>
          </ul>
          <Typography>
            In case of intense physiologic uptake in the rectum, rescale to an SUV scale of 0-20 and reassess.
          </Typography>
          <img
            className={classes.image2}
            src={`${BASE_URL}${IMAGE_PATHS.LYMPHATIC_DRAINAGE_SITES.path}`}
            alt={`${IMAGE_PATHS.LYMPHATIC_DRAINAGE_SITES.alt}`}
          />
          <Typography className={classes.imageText}>
            <strong>Primary lymphatic drainage sites of the prostate</strong>
            <sup className={classes.superscript}>3</sup>
          </Typography>
          <div className={classes.findingsPanel}>
            <FindingsPanel
              trainingId={2}
              module="poslumaInterpretation"
              page={12}
              // setNextDisabledFindings={setNextDisabledFindings}
            />
          </div>
          <div className={classes.caseStudy}>
            <CaseStudy
              trainingId={2}
              module="poslumaInterpretation"
              page={12}
              index={1}
              setNextDisabledCS={setNextDisabledCS}
            />
          </div>
          <Typography className={classes.abbreviations}>Abbreviations:</Typography>
          <Typography className={classes.abbreviationContent}>LN, lymph node</Typography>
        </div>
      </div>
      <LearningCheckQuestions
        module="poslumaInterpretation"
        page={12}
        setNextDisabled={setNextDisabled}
        trainingId={2}
      />
    </>
  );
};

export default InterpretationPage12;
